// Admin modal: list all users (customer/driver/admin/restaurant), search, suspend/reactivate, delete.
import { useCallback, useEffect, useState } from "react";
import api, { extractError } from "@/api/client";
import { X, Users, Search, Trash2, RefreshCw, Shield, User as UserIcon, Bike, Store, Power, PowerOff } from "lucide-react";

const ROLE_LABEL = { customer: "زبون", driver: "سائق", admin: "مدير", restaurant: "مطعم" };
const ROLE_ICON  = { customer: UserIcon, driver: Bike, admin: Shield, restaurant: Store };

export default function AdminUsersModal({ onClose, currentAdminId }) {
  const [users, setUsers] = useState([]);
  const [role, setRole] = useState("");   // "" = all
  const [q, setQ] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [roleCounts, setRoleCounts] = useState({});
  const [activeCounts, setActiveCounts] = useState({});
  const [totalAll, setTotalAll] = useState(0);

  const load = useCallback(async () => {
    setBusy(true); setErr("");
    try {
      const qs = new URLSearchParams();
      if (role) qs.set("role", role);
      if (q.trim()) qs.set("q", q.trim());
      const { data } = await api.get(`/admin/users?${qs.toString()}`);
      setUsers(data.users || data);
      if (data.role_counts) setRoleCounts(data.role_counts);
      if (data.active_counts) setActiveCounts(data.active_counts);
      if (typeof data.total === "number") setTotalAll(data.total);
    } catch (e) { setErr(extractError(e)); }
    finally { setBusy(false); }
  }, [role, q]);

  useEffect(() => { load(); }, [load]);

  const remove = async (u) => {
    if (u.id === currentAdminId) { setErr("لا يمكنك حذف حسابك"); return; }
    if (!window.confirm(`حذف المستخدم "${u.name}" (${u.email})؟\nهذا الإجراء لا يمكن التراجع عنه.`)) return;
    setErr("");
    try {
      await api.delete(`/admin/users/${u.id}`);
      setUsers((arr) => arr.filter((x) => x.id !== u.id));
    } catch (e) { setErr(extractError(e)); }
  };

  const toggleActive = async (u) => {
    try {
      const { data } = await api.patch(`/admin/users/${u.id}`, { active: !u.active });
      setUsers((arr) => arr.map((x) => (x.id === u.id ? { ...x, ...data } : x)));
    } catch (e) { setErr(extractError(e)); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-auto" data-testid="admin-users-modal">
      <div className="bg-white w-full max-w-5xl rounded-2xl talabya-shadow-soft overflow-hidden my-6">
        <div className="flex items-center justify-between px-5 py-4 bg-gradient-to-l from-primary-brand to-blue-500 text-white">
          <div className="flex items-center gap-3">
            <Users size={20}/>
            <div className="font-black text-lg" style={{fontFamily:"Cairo"}}>إدارة المستخدمين</div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-lg bg-white/15 hover:bg-white/25 flex items-center justify-center" data-testid="admin-users-close">
            <X size={16}/>
          </button>
        </div>

        <div className="px-5 pt-4 pb-2 grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="admin-users-stats">
          <StatCard label="الإجمالي" count={totalAll} active={undefined} testId="stat-all"/>
          <StatCard label="زبائن" count={roleCounts.customer || 0} active={activeCounts.customer || 0} testId="stat-customer" tone="blue"/>
          <StatCard label="سائقين" count={roleCounts.driver || 0} active={activeCounts.driver || 0} testId="stat-driver" tone="amber"/>
          <StatCard label="مطاعم" count={roleCounts.restaurant || 0} active={activeCounts.restaurant || 0} testId="stat-restaurant" tone="green"/>
          <StatCard label="مسؤولين" count={roleCounts.admin || 0} active={activeCounts.admin || 0} testId="stat-admin" tone="purple"/>
        </div>

        <div className="px-5 pt-3 pb-3 flex flex-wrap items-center gap-2 border-b border-[var(--border)]">
          {[{k:"", l:"الكل", n: totalAll},
            {k:"customer", l:"زبائن", n: roleCounts.customer || 0},
            {k:"driver", l:"سائقين", n: roleCounts.driver || 0},
            {k:"restaurant", l:"مطاعم", n: roleCounts.restaurant || 0},
            {k:"admin", l:"مسؤولين", n: roleCounts.admin || 0}].map((t) => (
            <button key={t.k || "all"} onClick={() => setRole(t.k)}
              className={`px-3 py-1.5 rounded-lg text-xs font-black flex items-center gap-1.5 ${role === t.k ? "bg-primary-brand text-white" : "bg-[var(--surface-alt)] text-[var(--text-primary)]"}`}
              data-testid={`admin-users-filter-${t.k || "all"}`}>
              <span>{t.l}</span>
              <span className={`text-[10px] font-black px-1.5 py-0.5 rounded-full ${role === t.k ? "bg-white/25 text-white" : "bg-primary-brand text-white"}`}>
                {t.n}
              </span>
            </button>
          ))}
          <div className="relative flex-1 min-w-[180px]">
            <Search size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]"/>
            <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="بحث بالبريد أو الاسم…"
              className="w-full pr-9 pl-3 py-1.5 rounded-lg border border-[var(--border)] text-sm focus:outline-none focus:border-primary-brand"
              data-testid="admin-users-search"/>
          </div>
          <button onClick={load} disabled={busy} className="px-3 py-1.5 rounded-lg bg-white border border-[var(--border)] text-xs font-bold hover:bg-[var(--surface-alt)] disabled:opacity-50" data-testid="admin-users-refresh">
            <RefreshCw size={12} className={`inline-block ${busy ? "animate-spin" : ""}`}/> تحديث
          </button>
        </div>

        {err && <div className="mx-5 mt-3 text-sm bg-red-50 text-red-600 px-3 py-2 rounded-lg" data-testid="admin-users-error">{err}</div>}

        <div className="p-5 max-h-[65vh] overflow-auto">
          <table className="w-full text-sm">
            <thead className="text-primary-brand bg-primary-light">
              <tr>
                <th className="px-2 py-2 text-right font-black">الاسم</th>
                <th className="px-2 py-2 text-right font-black">البريد</th>
                <th className="px-2 py-2 text-right font-black">الهاتف</th>
                <th className="px-2 py-2 text-right font-black">الدور</th>
                <th className="px-2 py-2 text-right font-black">الحالة</th>
                <th className="px-2 py-2 text-right font-black">إجراءات</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const Icon = ROLE_ICON[u.role] || UserIcon;
                const isSelf = u.id === currentAdminId;
                return (
                  <tr key={u.id} className="border-b border-[var(--border)] hover:bg-[var(--surface-alt)]" data-testid={`admin-user-row-${u.id}`}>
                    <td className="px-2 py-2 font-bold">{u.name}</td>
                    <td className="px-2 py-2 text-xs" dir="ltr">{u.email}</td>
                    <td className="px-2 py-2 text-xs">{u.phone || "—"}</td>
                    <td className="px-2 py-2">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary-light text-primary-brand text-xs font-black">
                        <Icon size={11}/> {ROLE_LABEL[u.role] || u.role}
                      </span>
                    </td>
                    <td className="px-2 py-2 text-xs">
                      {u.active === false
                        ? <span className="text-red-600 font-black">معطّل</span>
                        : <span className="text-green-600 font-black">نشط</span>}
                    </td>
                    <td className="px-2 py-2">
                      {!isSelf ? (
                        <div className="flex gap-1">
                          <button onClick={() => toggleActive(u)}
                            className={`w-8 h-8 rounded-lg flex items-center justify-center ${u.active === false ? "bg-green-50 text-green-600 hover:bg-green-100" : "bg-amber-50 text-amber-600 hover:bg-amber-100"}`}
                            title={u.active === false ? "تفعيل" : "تعطيل"}
                            data-testid={`admin-user-toggle-${u.id}`}>
                            {u.active === false ? <Power size={13}/> : <PowerOff size={13}/>}
                          </button>
                          <button onClick={() => remove(u)}
                            className="w-8 h-8 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 flex items-center justify-center"
                            title="حذف"
                            data-testid={`admin-user-delete-${u.id}`}>
                            <Trash2 size={13}/>
                          </button>
                        </div>
                      ) : (
                        <span className="text-[10px] text-[var(--text-secondary)]">حسابك</span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {users.length === 0 && !busy && (
                <tr><td colSpan="6" className="text-center text-[var(--text-secondary)] py-8">لا توجد نتائج</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}


const TONE_CLS = {
  default: "bg-[var(--surface-alt)] border-[var(--border)]",
  blue:    "bg-blue-50 border-blue-200",
  amber:   "bg-amber-50 border-amber-200",
  green:   "bg-emerald-50 border-emerald-200",
  purple:  "bg-purple-50 border-purple-200",
};

function StatCard({ label, count, active, testId, tone = "default" }) {
  return (
    <div
      className={`rounded-2xl p-3 border ${TONE_CLS[tone] || TONE_CLS.default}`}
      data-testid={testId}
    >
      <div className="text-[11px] text-[var(--text-secondary)] font-bold">{label}</div>
      <div className="mt-0.5 text-2xl font-black text-[var(--text-primary)]" style={{fontFamily:"Cairo"}}>{count}</div>
      {typeof active === "number" && active !== count && (
        <div className="text-[10px] text-[var(--text-secondary)] mt-0.5">
          <span className="text-emerald-600 font-black">{active}</span> نشط
        </div>
      )}
    </div>
  );
}
